import axios from 'axios';
import { toast } from 'react-toastify';
import { getAuthToken } from '../utils/sessionAuth.js';

function authHeaders() {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function toastForError(error) {
    const status = error.response?.status;
    const msg = error.response?.data?.message;
    if (status === 401) toast.error('Unauthorized');
    else if (status === 404) toast.error('Device not found');
    else toast.error(msg || 'Unknown server error');
}

// post command to server
async function postCommand(url, message) {
    try {
        const response = await axios.post(url, {}, { headers: authHeaders() });
        if (response.status === 200 && message) {
            toast.success(message);
        }
        return response;
    } catch (error) {
        toastForError(error);
        throw error;
    }
}

// post command to server w/ options
async function postCommandWithOptions(url, data, message) {
    try {
        const response = await axios.post(url, data, {
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        });
        if (response.status === 200 && message) {
            toast.success(message);
        }
        return response;
    } catch (error) {
        toastForError(error);
        throw error;
    }
}

function enableRemoteDesktop(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/enableRemoteDesktop/${udid}`;
    postCommand(API_URL, 'Enable remote desktop command has been issued.');
}

function clearPasscode(udid, platform) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/ios/clearPasscode/${udid}`;
    const options = {
        platform
    }
    const message = "Clear passcode command has been issued."; 
    postCommandWithOptions(API_URL, options, message);
}

function lockDevice(udid, phoneNumber, lockScreenMessage) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/lockDevice/${udid}`;
    const options = {
        phoneNumber,
        message: lockScreenMessage
    }
    const message = "Lock device command has been issued."; 
    postCommandWithOptions(API_URL, options, message);
}

function eraseDevice(udid, options = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/eraseDevice/${udid}`;
    const message = options.returnToService
        ? 'Erase mit Return to Service wurde ausgelöst.'
        : 'Erase device command has been issued.';
    return postCommandWithOptions(API_URL, options, message);
}

function disableRemoteDesktop(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/disableRemoteDesktop/${udid}`;
    postCommand(API_URL, 'Disable remote desktop command has been issued.');
}

function updateDeviceInventory(platform, udid, options = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/${platform}/updateInventory/${udid}`;
    return postCommand(API_URL, options.silent ? null : 'Inventory update command has been issued.');
}

function updateDevicesInventory(devices, options = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/updateInventory`;
    const message = options.silent
        ? null
        : `Inventur-Update für ${devices.length} Gerät(e) ausgelöst.`;
    return postCommandWithOptions(API_URL, { devices }, message);
}

function getAvailableSoftwareUpdates(udid, options = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/getAvailableSoftwareUpdates/${udid}`;
    return postCommand(API_URL, options.silent ? null : 'Get available software updates command has been issued.');
}

function scheduleOSUpdate(udid, productKey, installAction = 'DownloadOnly') {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/scheduleOSUpdate/${udid}`;
    const options = { productKey, installAction };
    const message = installAction === 'InstallASAP'
        ? 'Install update command has been issued.'
        : 'Download update command has been issued.';
    return postCommandWithOptions(API_URL, options, message);
}

function getOSUpdateStatus(udid, options = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/getOSUpdateStatus/${udid}`;
    return postCommand(API_URL, options.silent ? null : 'OS update status command has been issued.');
}

function installAvailableOSUpdate(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/installAvailableOSUpdate/${udid}`;
    postCommand(API_URL, 'Install available OS update command has been issued.');
}

function restartDevice(udid, notifyUser) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/restartDevice/${udid}`;
    const options = {
        notifyUser
    }
    const message = (notifyUser) ? "Restart device command has been issued, user will be notified.": "Restart device command has been issued."; 
    postCommandWithOptions(API_URL, options, message);
}

function shutdownDevice(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/shutdownDevice/${udid}`;
    const message = "Shutdown device command has been issued."; 
    postCommand(API_URL, message);
}

function installProfile(udid, profile) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/installProfile/${udid}`;
    const options = {
        profileObject: profile
    }
    const message = "The install profile command has been issued."; 
    postCommandWithOptions(API_URL, options, message);
}

function removeProfile(udid, identifier, profileName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/removeProfile/${udid}`;
    const options = {
        identifier,
        profileName
    }
    const message = "The remove profile command has been issued."; 
    postCommandWithOptions(API_URL, options, message);
}

function disableProfile(udid, identifier, profileName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/disableProfile/${udid}`;
    const options = {
        identifier,
        profileName
    };
    const message = 'Das Profil wurde deaktiviert und wird deinstalliert.';
    postCommandWithOptions(API_URL, options, message);
}

function enableProfile(udid, identifier, profileName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/enableProfile/${udid}`;
    const options = {
        identifier,
        profileName
    };
    const message = 'Das Profil wurde aktiviert.';
    postCommandWithOptions(API_URL, options, message);
}

function reapplyProfile(udid, identifier, profileName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/reapplyProfile/${udid}`;
    const options = { identifier };
    const message = profileName
        ? `${profileName} wird erneut gesetzt.`
        : 'Das Profil wird erneut gesetzt.';
    postCommandWithOptions(API_URL, options, message);
}

function uploadProfile(profile) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/uploadProfile`;
    const options = {
        profileObject: profile
    }
    const message = 'The configuration profile has been uploaded.'; 
    postCommandWithOptions(API_URL, options, message);
}

function renameDevice(udid, platform, newName, oldName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/renameDevice/${udid}`;
    const options = {
        newName,
        platform
    }
    const message = `Command has been issued to rename ${oldName} to ${newName}`; 
    return postCommandWithOptions(API_URL, options, message);
}

function enableLostMode(udid, { message, phoneNumber, footnote } = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/enableLostMode/${udid}`;
    return postCommandWithOptions(API_URL, { message, phoneNumber, footnote }, 'Lost Mode wurde aktiviert.');
}

function disableLostMode(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/disableLostMode/${udid}`;
    return postCommand(API_URL, 'Lost Mode wurde beendet.');
}

function playLostModeSound(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/playLostModeSound/${udid}`;
    return postCommand(API_URL, 'Piepton im Lost Mode wurde ausgelöst.');
}

function requestDeviceLocation(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/deviceLocation/${udid}`;
    return postCommand(API_URL, 'Standortabfrage wurde gesendet.');
}

function getActivationLockBypassCode(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/activationLockBypassCode/${udid}`;
    return postCommand(API_URL, 'Activation-Lock-Bypass-Code wird abgefragt.');
}

function clearActivationLockBypassCode(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/clearActivationLockBypassCode/${udid}`;
    return postCommand(API_URL, 'Activation-Lock-Bypass-Code wird gelöscht.');
}

function setTimeZone(udid, timeZone) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/setTimeZone/${udid}`;
    return postCommandWithOptions(API_URL, { timeZone }, `Zeitzone ${timeZone} wird gesetzt.`);
}

function setBluetooth(udid, enabled) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/setBluetooth/${udid}`;
    return postCommandWithOptions(
        API_URL,
        { enabled },
        enabled ? 'Bluetooth wird eingeschaltet.' : 'Bluetooth wird ausgeschaltet.'
    );
}

function refreshMacUserList(udid) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/userList/${udid}`;
    return postCommand(API_URL, 'UserList angefordert.');
}

function deleteMacUser(udid, userName, { forceDeletion = false } = {}) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/deleteUser/${udid}`;
    return postCommandWithOptions(
        API_URL,
        { userName, forceDeletion },
        `Löschen von Benutzer „${userName}“ angefordert.`
    );
}

function unlockMacUser(udid, userName) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/unlockUser/${udid}`;
    return postCommandWithOptions(
        API_URL,
        { userName },
        `Entsperren von Benutzer „${userName}“ angefordert.`
    );
}

function repairMacManagedAdminPassword(udid, password) {
    const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/mdm/commands/mac/repairManagedAdminPassword/${udid}`;
    return postCommandWithOptions(
        API_URL,
        password ? { password } : {},
        'Managed-Admin-Passwort wird per SetAutoAdminPassword gesetzt.'
    );
}

export { 
    enableRemoteDesktop,
    disableRemoteDesktop,
    updateDeviceInventory,
    updateDevicesInventory, 
    restartDevice,
    installProfile,
    clearPasscode,
    renameDevice,
    shutdownDevice,
    uploadProfile,
    lockDevice,
    removeProfile,
    disableProfile,
    enableProfile,
    reapplyProfile,
    eraseDevice,
    getAvailableSoftwareUpdates,
    scheduleOSUpdate,
    getOSUpdateStatus,
    installAvailableOSUpdate,
    enableLostMode,
    disableLostMode,
    playLostModeSound,
    requestDeviceLocation,
    getActivationLockBypassCode,
    clearActivationLockBypassCode,
    setTimeZone,
    setBluetooth,
    refreshMacUserList,
    deleteMacUser,
    unlockMacUser,
    repairMacManagedAdminPassword,
};
